'use client';

import { useConfig } from '@/app/contexts/ConfigContext';
import CustomLink from './CustomLink';
import {
  capitalizeFirstLetter,
  checkUrlCountry,
  getWordingWithdrawal,
  getWordingUnsubscribe,
  isFRAndAllopassInternetPlus,
  isMobileAppPortalHost,
  isStringLongerThanMaxLength,
  shouldShowContact,
  shouldShowImpressum,
  shouldShowUnsubscribe,
} from '@/helpers';
import { useEffect, useState } from 'react';
import { latoBold } from '@/helpers/fonts';

export default function Footer() {
  const { config, wording, selectedLanguage } = useConfig();
  const isMobileAppPortal = isMobileAppPortalHost(
    config.SERVICE_URL_LINK,
  );
  const selectedLanguageWordingUnsubscribe =
    getWordingUnsubscribe(selectedLanguage);
  const selectedLanguageWordingWithdrawal =
    getWordingWithdrawal(selectedLanguage);
  const shouldShowContactLink = shouldShowContact(
    config.SERVICE_URL_LINK,
  );
  const shouldShowImpressumLink = shouldShowImpressum(
    config.SERVICE_URL_LINK,
  );
  const shouldShowUnsubscribeLink = shouldShowUnsubscribe(
    config.SERVICE_URL_LINK,
  );

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Use to execute client side only code so next can recognize window
    setIsClient(true);
  }, []);

  const textSpecificStyle = () => {
    if (
      isClient &&
      window.innerWidth < 380 &&
      isStringLongerThanMaxLength(config?.SERVICE_NAME, 13)
    ) {
      return {
        fontSize: '30px',
      };
    }
  };

  return (
    <footer>
      <div
        className='bg-gray-800'
        style={{ backgroundColor: config.theme.backgroundColorFooter }}
      >
        <div className='py-9 xl:py-20 px-4 sm:px-12 flex flex-col xl:flex-row text-center xl:text-left sm:max-w-[1400px] max-w-[390px] mx-auto'>
          <div className='flex-1'>
            <div className='flex flex-row items-center xl:justify-start justify-center'>
              <img
                src={config.theme.logoSrc}
                alt={`${config?.SERVICE_NAME} logo`}
                style={{
                  maxWidth: '47px',
                  maxHeight: '47px',
                }}
              />
              <span
                className='text-white font-extrabold text-[33.72px] ml-2.5 overflow-hidden whitespace-nowrap'
                style={{
                  color: config.theme.textColorLight,
                  ...textSpecificStyle(),
                }}
              >
                {config?.SERVICE_NAME}
              </span>
            </div>
            <div
              className={`${latoBold.className} mt-5 text-stone-300 sm:text-[22.48px] text-lg font-medium capitalize sm:leading-loose leading-relaxed`}
              style={{ color: config.theme.textColorLightSecondary }}
            >
              {wording?.components_footer_text}
            </div>
          </div>
          <div className='flex-1 py-9 xl:py-0 xl:px-20'>
            <span
              className='text-white sm:text-[28.10px] font-bold sm:leading-[30.91px] text-2xl leading-relaxed'
              style={{ color: config.theme.textColorLight }}
            >
              {wording?.components_footer_info}
            </span>
            <div
              className={`${latoBold.className} mt-5 sm:mt-10 text-stone-300 sm:text-xl sm:leading-[29.34px] bg-gray-800 xl:text-left grid xl:grid-cols-1 xl:gap-2`}
              style={{
                color: config.theme.textColorLightSecondary,
                backgroundColor: config.theme.backgroundColorFooter,
              }}
            >
              <span>{config.COMPANY_NAME}</span>
              <span>{config.COMPANY_ADDRESS}</span>
              <span>
                {wording?.components_footer_registration_number}:{' '}
                {config?.REGISTRATION_NUMBER}
              </span>
              {!isMobileAppPortal && (
                <CustomLink
                  href={`mailto:${config?.SERVICE_CONTACT_EMAIL}`}
                  className='hover:underline'
                >
                  {config?.SERVICE_CONTACT_EMAIL}
                </CustomLink>
              )}
            </div>
          </div>
          <div className='flex-1'>
            <span
              className='text-white sm:text-[28.10px] font-bold sm:leading-[30.91px] text-2xl leading-relaxed'
              style={{ color: config.theme.textColorLight }}
            >
              {wording?.components_footer_legal}
            </span>
            <div
              className={`${latoBold.className} mt-5 sm:mt-10 text-stone-300 sm:text-xl sm:leading-[29.34px] bg-gray-800 xl:text-left grid xl:grid-cols-1 xl:gap-5`}
              style={{ backgroundColor: config.theme.backgroundColorFooter }}
            >
              <CustomLink
                className='text-primary hover:underline'
                style={{ color: config.theme.accentColorOne }}
                href='/terms'
              >
                {wording?.components_footer_terms}
              </CustomLink>
              <CustomLink
                className='text-primary hover:underline'
                style={{ color: config.theme.accentColorOne }}
                href='/privacy-policy'
              >
                {capitalizeFirstLetter(
                  wording!.components_footer_privacy ?? '',
                )}
              </CustomLink>
              {shouldShowContactLink && (
                <CustomLink
                  className='text-primary hover:underline'
                  style={{ color: config.theme.accentColorOne }}
                  href='/contact'
                >
                  Kontakt
                </CustomLink>
              )}
              {shouldShowImpressumLink && (
                <>
                  <CustomLink
                    className='text-primary hover:underline'
                    style={{ color: config.theme.accentColorOne }}
                    href='/impressum'
                  >
                    Impressum
                  </CustomLink>
                </>
              )}
              {shouldShowUnsubscribeLink && (
                <CustomLink
                  className='text-primary hover:underline'
                  style={{ color: config.theme.accentColorOne }}
                  href='/unsubscribe'
                >
                  {selectedLanguageWordingUnsubscribe?.pageTitle}
                </CustomLink>
              )}
              {(isFRAndAllopassInternetPlus(
                config.SERVICE_URL_LINK,
              ) ||
                checkUrlCountry(
                  config.SERVICE_URL_LINK,
                  'testing.',
                )) &&
                !isMobileAppPortal && (
                <CustomLink
                  className='text-primary hover:underline'
                  style={{ color: config.theme.accentColorOne }}
                  href='/account'
                >
                  Mon Compte
                </CustomLink>
              )}
              {!isMobileAppPortal && (
                <CustomLink
                  className='text-primary hover:underline'
                  style={{ color: config.theme.accentColorOne }}
                  href='/withdrawal'
                >
                  {selectedLanguageWordingWithdrawal.pageTitle}
                </CustomLink>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='text-center'>
        <div
          className={`${latoBold.className} text-neutral-500 sm:text-[22.48px] font-medium capitalize sm:leading-[54.52px] text-lg leading-[43.65px]`}
          style={{ color: config.theme.textColorLightSecondary }}
        >
          Copyright ©{' '}
          <span
            className='text-secondary'
            style={{ color: config.theme.accentColorTwo }}
          >
            {config?.SERVICE_NAME}
          </span>{' '}
          | {new Date().getFullYear()}
        </div>
      </div>
    </footer>
  );
}
